import { RouteComponentProps } from 'react-router-dom';
import { Permission } from 'utils/user/types';
import { AccessError } from 'routes/types';
import { AxiosError } from 'axios';
import { Nullable } from 'utils/types';

export interface Dict<T = any> {
  [key: string]: T;
}

export type Function<T> = (...args: any[]) => T;

export enum ApplicantAvailable {
  applicant1 = 'applicant_1',
  applicant2 = 'applicant_2',
}

export type ApplicantKeyDict<T> = {
  [ApplicantAvailable.applicant1]: T;
  [ApplicantAvailable.applicant2]?: T;
};

export const CurrencySymbol = '£';

export interface Option<TParams extends object = object> {
  option: string;
  value: string;
  name?: string;
  id?: string;
  disabled?: boolean;
  params?: TParams;
}

export type OptionWithName = {
  name: string;
  id: string;
};

export enum ApplicationStatuses {
  PARTIAL = 'Partial',
  SUBMITTED = 'Submitted',
  PRODUCT_RESELECTION = 'Product Reselection',
  QUOTED = 'Quote',
  REFERRED = 'Referred',
  MBC = 'MBC',
  FMA = 'FMA',
  DROPPED = 'Dropped',
  DECLINED = 'Declined',
  RECEIVED = 'Application received',
  IN_PROGRESS = 'In progress',
  OFFER = 'Offer',
  SOLICITORS = 'Solicitors',
  FOUNDS_RELEASED = 'Funds Released',
  COMPLETION = 'Completion',
}

export const EditableApplicationStatuses = [
  ApplicationStatuses.PARTIAL.toString(),
  ApplicationStatuses.QUOTED.toString(),
  ApplicationStatuses.MBC.toString(),
  ApplicationStatuses.FMA.toString(),
];

export type ApplicationStatus = ApplicationStatuses.PARTIAL | ApplicationStatuses.SUBMITTED;

export enum TelephoneTypes {
  LANDLINE = 'landline',
  MOBILE = 'mobile',
}

export const LMCPhoneNumber = '020 4525 7754';

export type ReducerItemsState<T> = {
  pending: boolean;
  items: T[];
  error: string;
};

export type ReducerDataState<T> = {
  pending: boolean;
  data: T;
  error: string;
  loaded?: boolean;
  axiosError: AxiosError | null;
};

export type ResponseAddress = {
  postcode: Nullable<string>;
  flatNumber: Nullable<string>;
  buildingName: Nullable<string>;
  buildingNumber: Nullable<string>;
  street: Nullable<string>;
  city: Nullable<string>;
  county: Nullable<string>;
};

export interface AddressResponse {
  postcode: Nullable<string>;
  flatNumber: Nullable<string>;
  buildingName: Nullable<string>;
  buildingNumber: Nullable<string>;
  street: Nullable<string>;
  city: Nullable<string>;
  county: Nullable<string>;
}

export interface AttachedFile {
  name: string;
  type: string;
}

export type User = {
  name: string;
};

export type PageComponentProps<T = any> = RouteComponentProps<T> & {
  permission: Permission;
  handleError: (errorHandler: CallableFunction) => AccessError;
};

type ApplicantResponseData = {
  first_name: string;
  surname: string;
  title: string;
  date_of_birth: string;
};

export type ApplicationResponseData = {
  id: number;
  application_id: number;
  case_no: string;
  applicant_1: ApplicantResponseData;
  applicant_2?: ApplicantResponseData | null;
  mortgage_property: AddressResponse;
  esis: {
    application_id: number;
    filename: string;
    esis_file: string;
    document_date_created: string;
  };
  created_at: string;
  updated_at: string;
  date_submitted: Nullable<string>;
  status: string;
  is_referral: boolean;
  product: string;
  number_of_applicants: number;
  loan_amount_required: number;
  estimated_property_value: number;
  completion_fee: string;
  loan_purpose: string;
  capital_raise_purpose: Nullable<string>;
  credit_intermediary_fee: boolean;
  fee_charged: Nullable<number>;
  loan_amount_outstanding: Nullable<number>;
  current_mortgage_provider: Nullable<string>;
  monthly_basic_spend: Nullable<number>;
  applicants_married_or_in_civil_partnership: Nullable<boolean>;
  declarations_confirmed: Nullable<boolean>;
  terms_and_conditions_accepted: Nullable<boolean>;
  is_soft_check_confirmed: boolean;
  is_hard_check_confirmed: boolean;
  is_mortgage_property_condition_confirmed: boolean;
  profiling_consent: boolean;
  sort_code: Nullable<string>;
  acc_no: Nullable<string>;
  account_name: Nullable<string>;
  name_of_bank: Nullable<string>;
  solicitor_provided_by_lmc: Nullable<string>;
  solicitor_name: Nullable<string>;
  solicitor_address: Nullable<string>;
  solicitor_contact_number: Nullable<string>;
  other_occupants_at_the_property: Nullable<string>;
  is_affordable: boolean;
  soft_credit_check_result: any | null;
  hard_credit_check_result: any | null;
  bwa_data: any;
  cross_core_id_check_data: any;
  soft_credit_check_data: any;
  hard_credit_check_data: any;
  application_esis: any | null;
  offer_letter: any | null;
  direct_debit_form: any | null;
  rights_to_reside_applicant_1: any | null;
  rights_to_reside_applicant_2: any | null;
  valuation: any | null;
  bank_statement: any | null;
  created_by: number;
  vendor: any | null;
  marketing_preferences: any | null;
};

export type BooleanString = 'true' | 'false';

export type PropertyType = 'non_property' | 'other_property' | 'btl_property';

export enum KEY_BINDINGS {
  ENTER = 13,
  ESC = 27,
  SPACE = 32,
}

export const LMC_ACCEPTED_APPLICANT_AGE_START = 49.75;
export const LMC_ACCEPTED_APPLICANT_AGE = 50;

export enum ERROR_CODES {
  NOT_FOUND = 404,
  BAD_REQUEST = 400,
  SERVICE_UNAVAILABLE = 503,
}

export type TabConfig = {
  id: string;
  label: string;
  isDisabled?: boolean;
};
